// src/pages/auth/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Card, Typography, Space } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { LockOutlined, CheckCircleOutlined, KeyOutlined } from '@ant-design/icons';
import { useResetPasswordMutation, useResetPasswordSendLinkMutation, useVerifyResetCodeMutation } from '../../store/api/apiSlice';
import resetPasswordImage from '../../assets/images/fi_2665311.png'; // Ensure this path is correct

const { Title, Text } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCodeVerified, setIsCodeVerified] = useState(true);
  const [resetCode, setResetCode] = useState('');
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(false);
  
  // API mutations
  const [verifyResetCode] = useVerifyResetCodeMutation();
  const [resetPassword] = useResetPasswordMutation();

  // Extract code and role from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const role = params.get('role');
    if (code) {
      setResetCode(code);
      form.setFieldsValue({ code });
    }
    
    if (role) {
      setUserRole(role);
    }
  }, [location, form]);

  // Handle code verification
  const handleVerifyCode = async () => {
    try {
      setLoading(true);
      await form.validateFields(['code']);
      const code = form.getFieldValue('code');
      debugger
      // Create payload with code and role if available
      const payload = { code };
      if (userRole) {
        payload.role = userRole;
      }
      
      // Call verify reset code API
      const response = await verifyResetCode(payload).unwrap();
      
      if (response.success) {
        message.success('Code verified successfully');
        setIsCodeVerified(true);
      } else {
        message.error(response.message || 'Invalid or expired code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      message.error(error.data?.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset submission
  const handleResetPassword = async (values) => {
    try {
      setLoading(true);
      
      // Create payload with code, role and new password
      const payload = {
        code: resetCode,
        new_password: values.password
      };
      
      // Include role if available
      if (userRole) {
        payload.role = userRole;
      }
      
      // Call reset password API
      const response = await resetPassword(payload).unwrap();
      
      if (response.success) {
        message.success('Password reset successfully');
        setTimeout(() => navigate('/auth/login'), 2000);
      } else {
        message.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      message.error(error.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md rounded-2xl shadow-lg ">
        <div className="flex flex-col items-center">
          <img src={resetPasswordImage} alt="Reset Password" className="h-24 w-24 mb-4" />
          
          <Title level={3} className="text-center mb-2">
            {isCodeVerified ? 'Set New Password' : 'Verify Reset Code'}
          </Title>
          
          <Text className="text-gray-500 text-center mb-6">
            {isCodeVerified 
              ? 'Please enter your new password below' 
              : 'Enter the reset code from your email to continue'}
          </Text>
          
          {userRole && (
            <Text className="text-blue-600 font-medium mb-4">
              Account Type: {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
            </Text>
          )}
          
          <Form
            form={form}
            name="reset_password"
            layout="vertical"
            className="w-full"
            onFinish={isCodeVerified ? handleResetPassword : handleVerifyCode}
          >
            {!isCodeVerified ? (
              <Form.Item
                name="code"
                rules={[{ required: true, message: 'Please enter your reset code' }]}
              >
                <Input 
                  prefix={<KeyOutlined className="site-form-item-icon" />}
                  placeholder="Enter reset code"
                  size="large"
                  className="rounded-xl py-3"
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: 'Please enter your new password' },
                    { min: 8, message: 'Password must be at least 8 characters' }
                  ]}
                >
                  <Input.Password 
                    prefix={<LockOutlined className="site-form-item-icon" />}
                    placeholder="New password"
                    size="large"
                    className="rounded-xl py-3"
                  />
                </Form.Item>
                
                <Form.Item
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Please confirm your password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password 
                    prefix={<LockOutlined className="site-form-item-icon" />}
                    placeholder="Confirm password"
                    size="large"
                    className="rounded-xl py-3"
                  />
                </Form.Item>
              </>
            )}
            
            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={isCodeVerified ? <LockOutlined /> : <CheckCircleOutlined />}
                className="w-full h-12 rounded-xl mt-4"
              >
                {isCodeVerified ? 'Reset Password' : 'Verify Code'}
              </Button>
            </Form.Item>
            
            <div className="text-center mt-4">
              <Button type="link" onClick={() => navigate('/auth/login')}>
                Back to Login
              </Button>
            </div>
          </Form>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;